export type ApplicationAnswers = {
  email: string
  designLink: string
  location?: string
  meetup: boolean
  twitter?: string
  linkedin?: string
  sweaterSize: string
}

export type SubmitResult =
  | { ok: true }
  | {
      ok: false
      kind: 'validation' | 'rate_limited' | 'server' | 'network'
      message: string
    }

const ENDPOINT = 'https://www.api.magicpatterns.com/applications/ambassador'

function normalizeTwitterHandle(raw: string): string {
  let h = raw.trim()
  if (!h) return ''
  // If it's a URL, pull the last path segment
  if (/^https?:\/\//i.test(h)) {
    try {
      const url = new URL(h)
      const parts = url.pathname.split('/').filter(Boolean)
      if (parts.length > 0) h = parts[parts.length - 1]
    } catch {
      // ignore
    }
  }
  // Strip leading @ and any query/anchor leftovers
  h = h.replace(/^@+/, '').split(/[?#]/)[0]
  return h
}

function buildBody(answers: ApplicationAnswers, honeypot: string) {
  const a: Record<string, unknown> = {
    email: answers.email.trim().toLowerCase(),
    design_link: answers.designLink.trim(),
    open_to_meetup: answers.meetup,
    sweater_size: answers.sweaterSize,
    hp_field: honeypot,
  }
  if (answers.location && answers.location.trim()) {
    a.location = answers.location.trim()
  }
  if (answers.twitter && answers.twitter.trim()) {
    a.twitter_handle = normalizeTwitterHandle(answers.twitter)
  }
  if (answers.linkedin && answers.linkedin.trim()) {
    a.linkedin_url = answers.linkedin.trim()
  }
  return {
    form_version: '2026-05-20',
    submitted_at: new Date().toISOString(),
    source: 'magicpatterns.com/ambassadors',
    answers: a,
  }
}

export async function submitApplication(
  answers: ApplicationAnswers,
  honeypot: string,
): Promise<SubmitResult> {
  const body = buildBody(answers, honeypot)
  let res: Response
  try {
    res = await fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
  } catch {
    return {
      ok: false,
      kind: 'network',
      message: 'Something went wrong. Please try again.',
    }
  }

  let data: { status?: string; message?: string } = {}
  try {
    data = await res.json()
  } catch {
    // server returned non-JSON
  }

  if (res.status === 200 && data.status === 'ok') {
    return { ok: true }
  }
  if (res.status === 400) {
    return {
      ok: false,
      kind: 'validation',
      message:
        data.message || 'Please double-check your answers and try again.',
    }
  }
  if (res.status === 429) {
    return {
      ok: false,
      kind: 'rate_limited',
      message:
        data.message ||
        'Too many submissions from this network. Please try again in an hour.',
    }
  }
  return {
    ok: false,
    kind: 'server',
    message: 'Something went wrong. Please try again.',
  }
}
